import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { Trans } from 'react-i18next';
import { MainLayout } from '../../MainLayout';
import { PageHeading } from '../../layout/PageHeading';
import { PianoIdentity } from '../../layout/PianoIdentity';
import { PrintButton } from '../../layout/PrintButton';
import { AnalysisFooter } from '@/components/analyze/key-reading/AnalysisFooter';
import { KeyReadingStrip } from '@/components/analyze/key-reading/KeyReadingStrip';
import { useKeyboardStats } from '@/components/analyze/key-reading/hooks/use-keyboard-stats';
import { serieValueAt, type ChartReadingContext } from '@/components/charts/interfaces';
import { selectComputedStrikeWeightRatio, selectStrikeWeightRatio, StrikeWeightRatioChart } from '@/components/charts/StrikeWeightRatioChart';
import { useAnalyzedKeyboard } from '@/hooks/keyboard/use-analyzed-keyboard';
import { useMeasureOptions } from '@/hooks/store/use-measure-options-store';
import { useTranslation } from '@/hooks/use-translation';
import { formatGrams, formatRatio, formatSignedRatio } from '@/lib/format/format-weight';
import { usePrintLandscape } from '@/hooks/use-print-page-size';
import { MeasureFirst } from '@/components/analyze/MeasureFirst';
export const AnalyzeStrikeWeightRatioPage = () => {
  const $ = _c(22);
  usePrintLandscape();
  const analyzedKeyboard = useAnalyzedKeyboard();
  const {
    currentLanguage,
    t
  } = useTranslation();
  const {
    useManualSWRMeasurements
  } = useMeasureOptions();
  const stats = useKeyboardStats(analyzedKeyboard, useManualSWRMeasurements ? selectComputedStrikeWeightRatio : selectStrikeWeightRatio);
  let t0;
  if ($[0] !== analyzedKeyboard || $[1] !== currentLanguage || $[2] !== stats || $[3] !== t) {
    t0 = t1 => {
      const {
        hoveredKey,
        measuredSerie,
        referenceSeries
      } = t1;
      const ratio = serieValueAt(measuredSerie, hoveredKey);
      const [manualSerie] = referenceSeries;
      const deviation = ratio === null || stats.median === null ? null : ratio - stats.median;
      return <KeyReadingStrip reading={{
        footnote: ratio === null ? undefined : t("1 g at the hammer weighs {{value}} at the key measuring point.", {
          value: formatGrams(ratio, currentLanguage)
        }),
        keyNumber: hoveredKey,
        noteName: analyzedKeyboard.getKeyByNumber(hoveredKey ?? 0)?.name ?? null,
        rows: [{
          isAccent: true,
          label: t("Computed ratio"),
          value: formatRatio(ratio, currentLanguage)
        }, ...(manualSerie ? [{
          label: t("Measured ratio"),
          value: formatRatio(serieValueAt(manualSerie, hoveredKey), currentLanguage)
        }] : []), {
          label: t("Deviation from median"),
          value: formatSignedRatio(deviation, currentLanguage)
        }]
      }} />;
    };
    $[0] = analyzedKeyboard;
    $[1] = currentLanguage;
    $[2] = stats;
    $[3] = t;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const renderReading = t0;
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <><PianoIdentity /><PrintButton /></>;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <em className="ptl-accent">Ratio</em>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t) {
    t3 = <PageHeading title={<Trans t={t} i18nKey="Strike Weight <1>Ratio</1>">Strike Weight {t2}</Trans>} />;
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <MeasureFirst />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== analyzedKeyboard || $[11] !== renderReading || $[12] !== useManualSWRMeasurements) {
    t5 = <StrikeWeightRatioChart emptyState={t4} keyboard={analyzedKeyboard} displayBothManualAndComputed={useManualSWRMeasurements} renderReading={renderReading} />;
    $[10] = analyzedKeyboard;
    $[11] = renderReading;
    $[12] = useManualSWRMeasurements;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t) {
    t6 = t("Design the ratio");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t6) {
    t7 = <AnalysisFooter action={{
      label: t6,
      to: "/design/strike-weight-ratio"
    }} />;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t3 || $[19] !== t5 || $[20] !== t7) {
    t8 = <MainLayout headerRight={t1}>{t3}{t5}{t7}</MainLayout>;
    $[18] = t3;
    $[19] = t5;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};